import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

const MOTIVATION_OPTIONS = [
  "Organizar melhor minhas decisões financeiras",
  "Profissionalizar meus investimentos",
  "Ter mais clareza sobre impostos e estrutura patrimonial",
  "Planejar o longo prazo com mais segurança",
  "Preparar sucessão ou proteção do patrimônio",
  "Outro",
];

const REAL_ESTATE_OPTIONS = [
  "Não possuo patrimônio imobiliário",
  "Até R$500 mil",
  "Entre R$500 mil e R$2 milhões",
  "Entre R$2 milhões e R$5 milhões",
  "Acima de R$5 milhões",
];

const FINANCIAL_WEALTH_OPTIONS = [
  "Até R$250 mil",
  "Entre R$250 mil e R$1 milhão",
  "Entre R$1 milhão e R$5 milhões",
  "Acima de R$5 milhões",
];

const MONTHLY_INCOME_OPTIONS = [
  "Até R$7 mil",
  "Entre R$7 mil e R$15 mil",
  "Entre R$15 mil e R$30 mil",
  "Entre R$30 mil e R$60 mil",
  "Acima de R$60 mil",
];

interface FormData {
  fullName: string;
  email: string;
  whatsapp: string;
  motivations: string[];
  realEstateWealth: string;
  financialWealth: string;
  monthlyIncome: string;
  expectations: string;
}

export const ContatoMultiStepForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    whatsapp: "",
    motivations: [],
    realEstateWealth: "",
    financialWealth: "",
    monthlyIncome: "",
    expectations: "",
  });

  const totalSteps = 6;
  const progress = (currentStep / totalSteps) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleMotivationChange = (option: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      motivations: checked
        ? [...prev.motivations, option]
        : prev.motivations.filter((m) => m !== option),
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const { error } = await supabase.from("leads").insert({
        full_name: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        whatsapp: formData.whatsapp.trim(),
        motivations: formData.motivations,
        real_estate_wealth: formData.realEstateWealth,
        financial_wealth: formData.financialWealth,
        monthly_income: formData.monthlyIncome,
        expectations: formData.expectations.trim(),
      });

      if (error) throw error;

      navigate("/obrigado");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Erro ao enviar",
        description: "Ocorreu um erro. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStep1Valid = formData.fullName && formData.email && formData.whatsapp;
  const isStep2Valid = formData.motivations.length > 0;
  const isStep3Valid = formData.realEstateWealth !== "";
  const isStep4Valid = formData.financialWealth !== "";
  const isStep5Valid = formData.monthlyIncome !== "";
  const isStep6Valid = formData.expectations !== "";

  return (
    <div className="surface-card p-8 md:p-10">
      <Progress value={progress} className="mb-8 h-2" />

      {/* ETAPA 1 — Dados de contato */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <h2 className="heading-sm mb-8 text-foreground">Dados de contato</h2>

          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-foreground">
              Nome completo
            </Label>
            <Input
              id="fullName"
              value={formData.fullName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, fullName: e.target.value }))
              }
              placeholder="Seu nome completo"
              className="bg-secondary border-border focus:border-primary"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground">
              E-mail
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="seu@email.com"
              className="bg-secondary border-border focus:border-primary"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="whatsapp" className="text-foreground">
              WhatsApp (com DDD)
            </Label>
            <Input
              id="whatsapp"
              value={formData.whatsapp}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, whatsapp: e.target.value }))
              }
              placeholder="(11) 99999-9999"
              className="bg-secondary border-border focus:border-primary"
            />
          </div>

          <p className="text-sm text-muted-foreground">
            Usaremos apenas para entrar em contato sobre sua solicitação.
          </p>

          <Button
            onClick={handleNext}
            disabled={!isStep1Valid}
            className="w-full btn-primary"
          >
            Continuar →
          </Button>
        </div>
      )}

      {/* ETAPA 2 — Motivação do diagnóstico */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <div>
            <h2 className="heading-sm mb-2 text-foreground">
              O que mais te motivou a solicitar esse diagnóstico?
            </h2>
            <p className="text-sm text-muted-foreground">
              Selecione todas que se aplicam
            </p>
          </div>

          <div className="space-y-4">
            {MOTIVATION_OPTIONS.map((option) => (
              <div key={option} className="flex items-center space-x-3">
                <Checkbox
                  id={option}
                  checked={formData.motivations.includes(option)}
                  onCheckedChange={(checked) =>
                    handleMotivationChange(option, checked as boolean)
                  }
                />
                <Label
                  htmlFor={option}
                  className="text-foreground cursor-pointer"
                >
                  {option}
                </Label>
              </div>
            ))}
          </div>

          <div className="flex gap-4">
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button
              onClick={handleNext}
              disabled={!isStep2Valid}
              className="flex-1 btn-primary"
            >
              Continuar →
            </Button>
          </div>
        </div>
      )}

      {/* ETAPA 3 — Patrimônio imobiliário */}
      {currentStep === 3 && (
        <div className="space-y-6">
          <h2 className="heading-sm mb-2 text-foreground">
            Considerando seu patrimônio imobiliário aproximado (ex: imóveis
            residenciais, comerciais, terrenos), em qual faixa você se encontra?
          </h2>

          <RadioGroup
            value={formData.realEstateWealth}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, realEstateWealth: value }))
            }
            className="space-y-4"
          >
            {REAL_ESTATE_OPTIONS.map((option) => (
              <div key={option} className="flex items-center space-x-3">
                <RadioGroupItem value={option} id={`real-estate-${option}`} />
                <Label
                  htmlFor={`real-estate-${option}`}
                  className="text-foreground cursor-pointer"
                >
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>

          <div className="flex gap-4">
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button
              onClick={handleNext}
              disabled={!isStep3Valid}
              className="flex-1 btn-primary"
            >
              Continuar →
            </Button>
          </div>
        </div>
      )}

      {/* ETAPA 4 — Patrimônio em aplicações financeiras */}
      {currentStep === 4 && (
        <div className="space-y-6">
          <h2 className="heading-sm mb-2 text-foreground">
            Considerando seu patrimônio em aplicações financeiras (ex: renda
            fixa, fundos, ações, previdência), em qual faixa você se encontra?
          </h2>

          <RadioGroup
            value={formData.financialWealth}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, financialWealth: value }))
            }
            className="space-y-4"
          >
            {FINANCIAL_WEALTH_OPTIONS.map((option) => (
              <div key={option} className="flex items-center space-x-3">
                <RadioGroupItem value={option} id={`financial-${option}`} />
                <Label
                  htmlFor={`financial-${option}`}
                  className="text-foreground cursor-pointer"
                >
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>

          <div className="flex gap-4">
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button
              onClick={handleNext}
              disabled={!isStep4Valid}
              className="flex-1 btn-primary"
            >
              Continuar →
            </Button>
          </div>
        </div>
      )}

      {/* ETAPA 5 — Renda mensal */}
      {currentStep === 5 && (
        <div className="space-y-6">
          <h2 className="heading-sm mb-2 text-foreground">
            Sua renda mensal aproximada hoje está em qual faixa?
          </h2>

          <RadioGroup
            value={formData.monthlyIncome}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, monthlyIncome: value }))
            }
            className="space-y-4"
          >
            {MONTHLY_INCOME_OPTIONS.map((option) => (
              <div key={option} className="flex items-center space-x-3">
                <RadioGroupItem value={option} id={`income-${option}`} />
                <Label
                  htmlFor={`income-${option}`}
                  className="text-foreground cursor-pointer"
                >
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>

          <div className="flex gap-4">
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button
              onClick={handleNext}
              disabled={!isStep5Valid}
              className="flex-1 btn-primary"
            >
              Continuar →
            </Button>
          </div>
        </div>
      )}

      {/* ETAPA 6 — Expectativa da conversa */}
      {currentStep === 6 && (
        <div className="space-y-6">
          <h2 className="heading-sm mb-2 text-foreground">
            O que você espera obter dessa conversa?
          </h2>

          <div className="space-y-2">
            <Textarea
              value={formData.expectations}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, expectations: e.target.value }))
              }
              placeholder="Compartilhe suas expectativas..."
              rows={4}
              className="bg-secondary border-border focus:border-primary resize-none"
            />
          </div>

          <p className="text-sm text-muted-foreground">
            Analisamos todas as solicitações individualmente. Caso haja
            alinhamento, entraremos em contato para dar continuidade à conversa.
          </p>

          <div className="flex gap-4">
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isStep6Valid || isSubmitting}
              className="flex-1 btn-primary"
            >
              {isSubmitting ? (
                "Enviando..."
              ) : (
                <>
                  Enviar solicitação <Check className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
